import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Compass, Users, ShieldCheck, Mail, MapPin } from 'lucide-react';
import { BackButton } from '../components/common/PageHeader';

const features = [
    {
        icon: CalendarDays,
        title: '找到一起做事的人',
        desc: '從聚餐、桌遊、看展到戶外體驗，讓想做的事不再因為少一個人而擱置。'
    },
    {
        icon: Compass,
        title: '找到合拍的運動與旅行夥伴',
        desc: '依照運動項目、地點、日期與旅行步調，找到適合一起行動的夥伴。'
    },
    {
        icon: Users,
        title: '先一起體驗，再自然認識',
        desc: '可以是一次同行、固定球友或新朋友。JoinU 不預設關係，讓連結從共同體驗中自然發生。'
    },
    {
        icon: ShieldCheck,
        title: '安心參與每一次相聚',
        desc: '透過主辦人審核、參與紀錄、旅伴評價、檢舉與封鎖機制，讓每一次相聚更安心。'
    }
];

const teamValues = [
    { label: '一起行動', desc: '把想法變成真實的共同體驗' },
    { label: '興趣相遇', desc: '從喜歡的事開始認識彼此' },
    { label: '自在連結', desc: '不預設關係，讓相處自然發生' },
    { label: '安心相聚', desc: '重視每位參與者的安全與感受' }
];

export default function AboutUs({ embedded = false }) {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div style={{ minHeight: embedded ? 'auto' : '100vh', background: embedded ? 'transparent' : 'linear-gradient(180deg, #eef4f9 0%, #f0faf9 15%, #f8fffe 40%, #ffffff 100%)', paddingBottom: embedded ? 0 : '80px', width: '100%', overflowX: 'clip', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
            {/* Header */}
            <div style={{
                display: embedded ? 'none' : 'block',
                background: 'linear-gradient(135deg, #4e8bca 0%, #6a9fd3 60%, #9cc2e6 100%)',
                padding: isMobile ? '28px 20px 32px' : '36px 32px 40px', borderRadius: '0 0 32px 32px',
                position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', top: '-30px', right: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ position: 'absolute', bottom: '-40px', left: '30px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />

                <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <BackButton onClick={() => navigate(-1)} label="返回上一頁" />
                    <div>
                        <h1 style={{ fontSize: isMobile ? '1.5rem' : '1.75rem', fontWeight: '800', color: '#ffffff', margin: '0 0 2px 0', letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            關於我們
                        </h1>
                        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.875rem', margin: 0 }}>
                            一起做事，也一起認識彼此
                        </p>
                    </div>
                </div>
            </div>

            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: embedded ? 0 : '0 16px' }}>
                <div style={{ maxWidth: embedded ? 'none' : '720px', margin: embedded ? 0 : '24px auto 0' }}>

                    <div style={{ padding: embedded ? 0 : '16px' }}>

                        {/* Brand Story */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: embedded ? '20px' : '24px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: '#4e8bca',
                                margin: '0 0 12px',
                                letterSpacing: '-0.01em'
                            }}>
                                我們的故事
                            </h3>
                            <p style={{
                                fontSize: '0.875rem',
                                color: '#374151',
                                lineHeight: 1.8,
                                margin: '0 0 12px'
                            }}>
                                生活裡總有很多想做的事——看一場展覽、打一場球、參加一個活動，或出發去一個沒去過的地方。有時候缺少的不是時間或興趣，只是一個願意一起的人。
                            </p>
                            <p style={{
                                fontSize: '0.875rem',
                                color: '#374151',
                                lineHeight: 1.8,
                                margin: '0 0 12px'
                            }}>
                                因此我們打造了 JoinU，讓每個人都能從活動、運動與旅行出發，找到興趣相近、時間合適、步調契合的同行夥伴。
                            </p>
                            <p style={{
                                fontSize: '0.875rem',
                                color: '#374151',
                                lineHeight: 1.8,
                                margin: '0 0 12px'
                            }}>
                                在這裡，你可以只是找人一起吃飯、打球、看展或旅行，也可以在一次次真實相處中，慢慢認識合拍的新朋友。關係不需要預設答案，一切從一起做一件事開始。
                            </p>
                            <p style={{
                                fontSize: '0.875rem',
                                color: '#374151',
                                lineHeight: 1.8,
                                margin: 0
                            }}>
                                JoinU 由「Join」與「U」組成，既代表加入彼此，也是一句邀請：「一起嗎？」我們希望讓每個原本只有自己的計畫，都有機會成為共同的體驗。
                            </p>
                        </div>

                        {/* Features */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: embedded ? '20px' : '24px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: '#4e8bca',
                                margin: '0 0 16px',
                                letterSpacing: '-0.01em'
                            }}>
                                我們的特色
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {features.map((f, i) => (
                                    <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '10px',
                                            background: '#eef4f9',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            {React.createElement(f.icon, { size: 20, color: '#4e8bca', strokeWidth: 1.9, 'aria-hidden': true })}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#1a1a1a', marginBottom: '4px' }}>
                                                {f.title}
                                            </div>
                                            <div style={{ fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6 }}>
                                                {f.desc}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Values */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: embedded ? '20px' : '24px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: '#4e8bca',
                                margin: '0 0 16px',
                                letterSpacing: '-0.01em'
                            }}>
                                我們的價值觀
                            </h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '12px'
                            }}>
                                {teamValues.map((v, i) => (
                                    <div key={i} style={{
                                        background: '#f9fafb',
                                        borderRadius: '12px',
                                        padding: '16px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{ fontSize: '0.875rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' }}>
                                            {v.label}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.5 }}>
                                            {v.desc}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Contact */}
                        <div style={{
                            background: 'white',
                            borderRadius: '16px',
                            padding: embedded ? '20px' : '24px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                fontWeight: '700',
                                color: '#4e8bca',
                                margin: '0 0 16px',
                                letterSpacing: '-0.01em'
                            }}>
                                聯絡我們
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '36px', height: '36px', borderRadius: '8px',
                                        background: '#eef4f9', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <Mail size={16} style={{ color: '#4e8bca' }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '2px' }}>客服信箱</div>
                                        <div style={{ fontSize: '0.875rem', color: '#1a1a1a', fontWeight: '500' }}>service@joinuworld.com</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{
                                        width: '36px', height: '36px', borderRadius: '8px',
                                        background: '#eef4f9', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                    }}>
                                        <MapPin size={16} style={{ color: '#4e8bca' }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: '2px' }}>總部所在地</div>
                                        <div style={{ fontSize: '0.875rem', color: '#1a1a1a', fontWeight: '500' }}>台灣</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Footer */}
                    <div style={{
                        display: embedded ? 'none' : 'block',
                        textAlign: 'center',
                        padding: '8px 16px 20px',
                        color: '#9ca3af',
                        fontSize: '0.75rem'
                    }}>
                        © {new Date().getFullYear()} JoinU. 把想做的事，變成一起做。
                    </div>
                </div>
            </div>
        </div>
    );
}
